/* eslint-disable complexity */
import {Typography} from '@hconnect/uikit'
import {Grid} from '@material-ui/core'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import {CircularProgress, IconButton} from '@mui/material'
import Box from '@mui/material/Box'
import classNames from 'classnames'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {Button} from '@hm/fundamental'

import {PageNames} from '../../../common/constants'
import {OrderCancellationBanner} from '../../../features/rmc/orderCancellation/components/OrderCancellationBanner'
import {OrderCancellationResultContainer} from '../../../features/rmc/orderCancellation/components/OrderCancellationResult'
import {useOrderCancellationBanner} from '../../../features/rmc/orderCancellation/hooks/useOrderCancellationBanner'
import {CancellationRequestStatus} from '../../../features/rmc/orderCancellation/types/cancellationRequest.types'
import {CloseAtModal} from '../../../features/rmc/orderManagement/components/CloseAtModal'
import {ManageOrderButton} from '../../../features/rmc/orderManagement/components/ManageOrderButton'
import {OrderChangeBanner} from '../../../features/rmc/orderManagement/components/OrderChangeBanner'
import {useManageOrderButtonVisibility} from '../../../features/rmc/orderManagement/hooks/useManageOrderButtonVisibility'
import {useOrderChangeBanner} from '../../../features/rmc/orderManagement/hooks/useOrderChangeBanner'
import {usePlusLoadAvailability} from '../../../features/rmc/orderManagement/hooks/usePlusLoadAvailability'
import {DeliveryProgressBar} from '../../../Molecules/DeliveryProgressBar'
import {Divider} from '../../../Molecules/Divider/Divider'
import {DownloadAllDeliveryNotes} from '../../../Molecules/DownloadAllDeliveryNotes/DownloadAllDeliveryNotes'
import {ETADeliveryTrucks} from '../../../Molecules/ETADeliveryTrucks/ETADeliveryTrucks'
import StatusIcon from '../../../Molecules/StatusIcons'
import StatusTooltip from '../../../Molecules/StatusTooltip'
import {ViewAllDeliveries} from '../../../Molecules/ViewAllDeliveries'
import {Features, useFeaturesState} from '../../../Organisms/Features'
import {statusFromOrderStatus} from '../../../Organisms/Orders'
import {Delivery} from '../../Finance/Invoices/Delivery.types'
import {FieldValue, LoadingNumberValue, QRCodeValue} from '../components'
import {usePermissions} from '../../../Permissions'
import {PermissionTypes} from '../../../Permissions/permissions.types'
import {VariationCodeBanner} from '../components/VariationCodeBanner'
import {useOrderCancellationButtonVisibility} from '../components/OrderCancellationButton/hooks/useOrderCancellationButtonVisibility'
import {OrderCancellationButton} from '../components/OrderCancellationButton/OrderCancellationButton'
import {useDeliveriesByOrderId} from '../hooks'
import {useDeliveryAnnotations} from '../OrderDetails/hooks'
import {Order} from '../Order.types'
import {usePlants} from '../OrderDetails/hooks'
import {OrderSummary} from '../OrderDetails/OrderDetailsHeader'
import {hasVariationCode, isTopBannerCode} from '../variationCode.utils'

import {useOrderDetailsSMStyles} from './OrderDetailsSMStyle'

type OrderDetailsSMProps = {
  deliveries?: Delivery[]
  orderHistoryDetails: JSX.Element
  order: Order
  expand: () => void
  close: () => void
  isMobile: boolean
  customerId: string
  analytics?: any
  bulkDownload: (
    deliveryIds: string[],
    orderIds: string[],
    ticketsCount: number,
    combined: boolean
  ) => void
  onManageOrder?: () => void
  onPlusLoad?: () => void
  onOrderDataChanged?: () => void
}
export const OrderDetailsSM: React.FC<OrderDetailsSMProps> = ({
  orderHistoryDetails,
  expand,
  close,
  isMobile,
  order,
  customerId,
  bulkDownload,
  onManageOrder,
  onPlusLoad,
  onOrderDataChanged
}) => {
  const {t} = useTranslation()
  const classes = useOrderDetailsSMStyles()
  const {getFeature} = useFeaturesState()
  const status = order && statusFromOrderStatus(order.orderStatus, order.shippingType)
  const isOrderCancellationButtonVisible = useOrderCancellationButtonVisibility(order)
  const {
    isVisible: isManageOrderButtonVisible,
    variant: manageOrderVariant,
    isLoading: isManageOrderButtonLoading
  } = useManageOrderButtonVisibility(order)
  const isPlusLoadAvailable = usePlusLoadAvailability(order)
  const [isCloseAtModalOpen, setIsCloseAtModalOpen] = useState(false)
  const [cancellationRequest, setCancellationRequest] = useState<
    CancellationRequestStatus | undefined
  >()
  const isGroupedDeliveries = getFeature('OrderIntakeGroupedDeliveries')

  const isFilteringOutCancelledDeliveries =
    getFeature('ExcludeAllCancelledDeliveries') || getFeature('ExcludeNotLoadedCancelledDeliveries')
  const isPumpServiceIncluded = getFeature('IncludePumpService')
  const useHaulierView = getFeature('UseHaulierView')
  const {data: deliveries, isFetching: deliveriesFetching} = useDeliveriesByOrderId(
    customerId,
    order?.orderId,
    isGroupedDeliveries,
    isFilteringOutCancelledDeliveries,
    order?.orderIds,
    isPumpServiceIncluded,
    useHaulierView
  )

  const {data: plant, isFetching: isPlantsFetching} = usePlants(
    order.businessLine,
    deliveries ? deliveries[0]?.plantName : null,
    deliveries ? deliveries[0]?.plantId : null
  )

  // Query customer notes for all deliveries in this order
  const {hasPermission} = usePermissions()
  const hasViewDeliveryAnnotationsPermission = hasPermission(PermissionTypes.VIEW_NOTES)

  const {deliveriesWithDeliveryAnnotationsCount: deliveriesWithNotesCount} = useDeliveryAnnotations(
    {
      orderId: order.orderId,
      enabled: hasViewDeliveryAnnotationsPermission
    }
  )

  const totalDeliveries = deliveries?.length || 0

  // Order change banner state
  const {
    showBanner: showOrderChangeBanner,
    variant: orderChangeBannerVariant,
    hasProcessingRequest: hasProcessingOrderChangeRequest,
    onRefresh: onRefreshOrderData,
    isRefreshing: isRefreshingOrderData
  } = useOrderChangeBanner({
    orderId: order?.orderId,
    orderStatus: order?.orderStatus,
    enabled: !!order?.orderId && !!order?.orderManagement?.isEnabled,
    onOrderDataChanged
  })
  const {
    showBanner: showCancellationBanner,
    variant: cancellationBannerVariant,
    hasProcessingRequest: hasProcessingCancellationRequest,
    onRefresh: onRefreshCancellationData,
    onDismiss: onDismissCancellationBanner,
    request: cancellationRequestStatus,
    isRefreshing: isRefreshingCancellationData,
    isDismissing: isDismissingCancellationBanner,
    isError: isErrorCancellationRequestStatus
  } = useOrderCancellationBanner({
    orderId: order.orderId,
    orderStatus: order.orderStatus,
    enabled: !cancellationRequest && isOrderCancellationButtonVisible,
    onOrderDataChanged
  })
  const hasProcessingRequest = hasProcessingOrderChangeRequest || hasProcessingCancellationRequest

  const [orderHistoryDetailsOpen, setOrderHistoryDetailsOpen] = useState(false)
  const qrCode = <QRCodeValue order={order} deliveries={deliveries} />
  // const totalNonServicesDeliveries = deliveries?.filter((delivery) => !delivery.isService).length

  React.useEffect(() => {
    setCancellationRequest(undefined)
  }, [order.orderId])

  if (cancellationRequest) {
    return (
      <OrderCancellationResultContainer
        orderId={order.orderId}
        requestId={cancellationRequest.requestId}
        onViewOrderDetails={() => setCancellationRequest(undefined)}
        onClose={() => setCancellationRequest(undefined)}
      />
    )
  }

  return (
    <Box p={2} height="100%" position="relative">
      {hasProcessingRequest && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255, 255, 255, 0.6)"
          zIndex={1}
          data-test-id="order-details-loading-overlay"
        >
          <CircularProgress size={40} />
        </Box>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">{t('order.orderDetails.summary')}</Typography>
        <Box>
          {isManageOrderButtonVisible && !hasProcessingRequest && (
            <IconButton
              className={classes.icon}
              size="large"
              onClick={onManageOrder}
              data-test-id={`manage-order-icon-${order.orderId}`}
              disabled={isManageOrderButtonLoading || hasProcessingOrderChangeRequest}
            >
              <EditIcon />
            </IconButton>
          )}
          {!isMobile && (
            <IconButton
              className={classes.icon}
              size="large"
              onClick={() => expand()}
              data-test-id="order-full-details"
            >
              <OpenInFullIcon />
            </IconButton>
          )}
          <IconButton
            className={classes.icon}
            size="large"
            onClick={() => close()}
            data-test-id="order-details-close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Divider />
      {showOrderChangeBanner && orderChangeBannerVariant && (
        <Box mb={2}>
          <OrderChangeBanner
            variant={orderChangeBannerVariant}
            onRefresh={onRefreshOrderData}
            isDismissible={orderChangeBannerVariant === 'changeRequestFailed'}
            isRefreshing={isRefreshingOrderData}
          />
        </Box>
      )}
      {showCancellationBanner && cancellationBannerVariant && (
        <Box mb={2}>
          <OrderCancellationBanner
            variant={cancellationBannerVariant}
            onRefresh={onRefreshCancellationData}
            onDismiss={onDismissCancellationBanner}
            isRefreshing={isRefreshingCancellationData}
            isDismissing={isDismissingCancellationBanner}
          />
        </Box>
      )}
      {hasVariationCode(order) && isTopBannerCode(order.variationCode!) && (
        <Box mb={2}>
          <VariationCodeBanner order={order} placement="top" />
        </Box>
      )}
      <Grid container>
        <Grid item xs={6}>
          <StatusTooltip
            title={t(`order.orderStatusLabel.tooltip.${status}`)}
            placement="top-start"
            data-test-id={`order-status-tooltip-${order.orderId}`}
          >
            <Box display="flex" alignItems="center" height="100%">
              <StatusIcon status={order.orderStatus} shippingType={order.shippingType} />
              <Box mx={0.5} />
              <Typography variant="h4">{t(`order.orderStatusLabel.${status}`)}</Typography>
            </Box>
          </StatusTooltip>
        </Grid>
        <Grid item xs={6} style={{marginTop: 8}}>
          <FieldValue
            dataTestId="shipping-type"
            label={t('order.orderDetails.shippingTypeLabel')}
            value={order.shippingType ? t(`order.orderDetails.${order.shippingType}`) : undefined}
            gutter
          />
        </Grid>
      </Grid>
      <OrderSummary
        order={order}
        plant={plant || undefined}
        isPlantsFetching={isPlantsFetching || deliveriesFetching}
        data-test-id="order-summary"
      />
      <LoadingNumberValue order={order} deliveries={deliveries} />
      {hasViewDeliveryAnnotationsPermission && (
        <FieldValue
          dataTestId="delivery-annotations-count"
          label={t('order.orderDetails.deliveryAnnotations')}
          value={`${deliveriesWithNotesCount}/${totalDeliveries}`}
          gutter
        />
      )}
      {qrCode ? (
        <>
          <Divider />
          {qrCode}
        </>
      ) : null}
      <DeliveryProgressBar order={order} />
      <ETADeliveryTrucks t={t} order={order} pageName={PageNames.ORDERS} />
      <DownloadAllDeliveryNotes t={t} bulkDownload={bulkDownload} order={order} />
      <ViewAllDeliveries t={t} totalDeliveries={deliveries?.length || 0} expand={expand} />

      <Features name="OrderHistory">
        <>
          <Box mt={1} className={classes.section} data-test-id="order-history-details">
            <Typography>{t('order.orderDetails.orderHistoryDetails')}</Typography>
            <IconButton
              className={classNames(classes.icon)}
              onClick={() => setOrderHistoryDetailsOpen(!orderHistoryDetailsOpen)}
            >
              {orderHistoryDetailsOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </IconButton>
          </Box>
          <Divider />
          <Box>{orderHistoryDetailsOpen && orderHistoryDetails}</Box>
        </>
      </Features>
      {isManageOrderButtonVisible && !hasProcessingRequest && (
        <Box pb={1} mt={4} key={`manage-${order.orderId}`}>
          <ManageOrderButton
            order={order}
            onClick={onManageOrder}
            variant={manageOrderVariant}
            isLoading={isManageOrderButtonLoading}
            disabled={hasProcessingOrderChangeRequest}
          />
        </Box>
      )}
      {isManageOrderButtonVisible && !hasProcessingRequest && isPlusLoadAvailable && (
        <Box pb={1} key={`plus-load-${order.orderId}`}>
          <Box width={1}>
            <Button
              variant="secondary"
              size="large"
              onClick={onPlusLoad}
              className="w-full"
              data-test-id={`plus-load-button-${order.orderId}`}
            >
              {t('orderIntake.rmc.orderManagement.plusLoadActions.addPlusLoad', 'Plus Load')}
            </Button>
          </Box>
        </Box>
      )}
      {isManageOrderButtonVisible && !hasProcessingRequest && isPlusLoadAvailable && (
        <Box pb={1} key={`close-at-${order.orderId}`}>
          <Box width={1}>
            <Button
              variant="secondary"
              size="large"
              onClick={() => setIsCloseAtModalOpen(true)}
              className="w-full"
              data-test-id={`close-at-button-${order.orderId}`}
            >
              {t(
                'orderIntake.rmc.orderManagement.closeAt.closeOrder',
                'Close order at {{maxQuantity}} {{unit}}',
                {
                  maxQuantity: order.schedules?.[0]?.scheduleQuantity,
                  unit: order.lineItems?.[0]?.quantityUom ?? 'M3'
                }
              )}
            </Button>
          </Box>
        </Box>
      )}
      {isOrderCancellationButtonVisible && (
        <Box pb={1} key={order.orderId}>
          <OrderCancellationButton
            order={order}
            disabled={hasProcessingRequest}
            onRequestSubmitted={setCancellationRequest}
            cancellationRequestStatus={cancellationRequestStatus}
            isErrorCancellationRequestStatus={isErrorCancellationRequestStatus}
          />
        </Box>
      )}

      <CloseAtModal
        isOpen={isCloseAtModalOpen}
        onClose={() => setIsCloseAtModalOpen(false)}
        order={order}
      />
    </Box>
  )
}
